// Hybrid RSA-OAEP + AES-256-GCM payload encryption for the NCLT backend.
// Same envelope format as the HC / DC services (nclt-backend/Encryption.md):
//   request  body or query: { key, iv, data }  (base64; AES-GCM tag appended to data)
//   response body:          { iv, data }       (only when X-Payload-Encrypted: 1)
//
// The RSA public key is fetched once per base URL from GET <base>/api/public-key
// and cached as a CryptoKey. A fresh AES-256 key is generated per request and
// kept in memory only long enough to decrypt that request's response.

const ab2b64 = (buf) => {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
};
const b642u8 = (b64) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

// Cache one public CryptoKey per base URL so a different gateway forces a refetch.
const publicKeyCache = new Map(); // baseUrl -> Promise<CryptoKey>

export async function getPublicKey(baseUrl) {
  if (publicKeyCache.has(baseUrl)) return publicKeyCache.get(baseUrl);

  const pending = (async () => {
    const res = await fetch(`${baseUrl}/api/public-key`);
    if (!res.ok) throw new Error(`public-key fetch failed: HTTP ${res.status}`);
    const pem = await res.text();
    const b64 = pem.replace(/-----[^-]+-----/g, "").replace(/\s/g, "");
    if (!b64) throw new Error("public-key response was empty");
    const der = b642u8(b64);
    return crypto.subtle.importKey(
      "spki",
      der.buffer,
      { name: "RSA-OAEP", hash: "SHA-256" },
      false,
      ["encrypt"]
    );
  })();

  publicKeyCache.set(baseUrl, pending);
  try {
    return await pending;
  } catch (err) {
    publicKeyCache.delete(baseUrl); // allow retry on the next call
    throw err;
  }
}

export async function encryptRequest(payloadObj, publicKey) {
  const aesKey = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify(payloadObj ?? {}));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    aesKey,
    plaintext
  ); // Web Crypto appends the 16-byte GCM tag automatically
  const rawAes = await crypto.subtle.exportKey("raw", aesKey);
  const encryptedAesKey = await crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    publicKey,
    rawAes
  );

  return {
    envelope: {
      key: ab2b64(encryptedAesKey),
      iv: ab2b64(iv),
      data: ab2b64(ciphertext),
    },
    aesKey, // keep to decrypt this request's response
  };
}

export async function decryptResponse(envelope, aesKey) {
  const iv = b642u8(envelope.iv);
  const data = b642u8(envelope.data);
  const plain = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    aesKey,
    data
  );
  return JSON.parse(new TextDecoder().decode(plain));
}

// Trust the X-Payload-Encrypted: 1 header, with the body shape as a fallback
// in case a proxy strips custom headers.
export function isEncryptedResponse(headers, body) {
  const flag =
    (headers && (headers["x-payload-encrypted"] || headers["X-Payload-Encrypted"])) ||
    null;
  if (flag === "1" || flag === 1) return true;
  return (
    body &&
    typeof body === "object" &&
    typeof body.iv === "string" &&
    typeof body.data === "string" &&
    Object.keys(body).length === 2
  );
}
